/**
 * Engraving record-keeping, loaded globally (engraving.js is product-page only,
 * but carts get edited everywhere). Keeps two durable copies of each engraving
 * design in sync with cart contents:
 *   1. The ENGRAVINGS: block appended to the cart note.
 *   2. One hidden cart attribute per design (_engraving_<id>).
 * Apps that rewrite the main product line (e.g. bundle discount apps) strip its
 * line-item properties; fee lines and cart attributes survive, so rebuilds prefer
 * the parent line but fall back to a fee line's mirrored copy of the design.
 */
(function () {
  /** Remove the existing ENGRAVINGS:/ENGRAVING: block (and any earlier-format remnant) so a fresh one can be appended. */
  function stripEngravingsSection(note) {
    if (!note) return '';
    var n = String(note);
    var markers = ['ENGRAVINGS:', 'ENGRAVING:'];
    for (var i = 0; i < markers.length; i++) {
      var idx = n.indexOf(markers[i]);
      if (idx !== -1) {
        n = n.substring(0, idx);
        break;
      }
    }
    return n.replace(/\s+$/, '');
  }

  /** Pick the best line to describe each engraving id: the parent if it still has its properties, else the first fee line. */
  function collectEngravingSources(cartItems) {
    var byId = {};
    var order = [];
    (cartItems || []).forEach(function (item) {
      var p = item.properties || {};
      var id = p._engraving_id;
      if (!id) return;
      var isFee = !!p._engraving_fee_line;
      if (!byId[id]) {
        byId[id] = { item: item, isFee: isFee, props: {} };
        order.push(id);
      } else if (byId[id].isFee && !isFee) {
        // Prefer the parent (non-fee) line for the heading (title/qty).
        byId[id].item = item;
        byId[id].isFee = false;
      }
      // Merge visible design props across every line sharing this id: the design is
      // split across the main line and its fee lines (and a bundle app may strip the
      // main line), so no single line is guaranteed to hold the whole picture.
      var bag = byId[id].props;
      Object.keys(p).forEach(function (k) {
        if (k.charAt(0) === '_' || k === 'Fee') return;
        var v = p[k];
        if (v == null || v === '') return;
        if (bag[k] == null || bag[k] === '') bag[k] = v;
      });
    });
    return { byId: byId, order: order };
  }

  function engravingDetailRows(p) {
    var rows = [];
    if (p['Engraving type']) rows.push('Type: ' + p['Engraving type']);
    if (p['Font']) rows.push('Font: ' + p['Font']);
    var s1 = [p['Line 1'], p['Line 2'], p['Line 3']].filter(Boolean);
    if (s1.length) rows.push('Side 1: ' + s1.join(' / '));
    var s2 = [p['Side 2 line 1'], p['Side 2 line 2'], p['Side 2 line 3']].filter(Boolean);
    if (s2.length) rows.push('Side 2: ' + s2.join(' / '));
    if (p['Logo previously submitted'] === 'Yes') rows.push('Logo: previously submitted');
    // Uploaded logo property: a Shopify cart file URL contains '/uploads/'.
    var logoVal = '';
    Object.keys(p).forEach(function (k) {
      var v = p[k];
      if (typeof v === 'string' && v.indexOf('/uploads/') !== -1) logoVal = v;
    });
    if (logoVal) rows.push('Logo file: ' + logoVal);
    return rows;
  }

  function buildEngravingsNoteSection(cartItems) {
    var sources = collectEngravingSources(cartItems);
    if (!sources.order.length) return '';

    var blocks = sources.order.map(function (id, i) {
      var src = sources.byId[id];
      var item = src.item;
      var title = item.product_title || 'Item';
      var variant = item.variant_title && item.variant_title !== 'Default Title'
        ? ' (' + item.variant_title + ')'
        : '';
      var qty = !src.isFee && item.quantity ? ' × ' + item.quantity : '';
      var heading = '#' + (i + 1) + ' — ' + title + variant + qty;
      if (src.isFee) heading += ' [recovered from fee line]';
      var rows = [heading];
      engravingDetailRows(src.props).forEach(function (r) { rows.push('  ' + r); });
      return rows.join('\n');
    });

    return 'ENGRAVINGS:\n\n' + blocks.join('\n\n');
  }

  function composeNoteWithEngravings(currentNote, cartItems) {
    var base = stripEngravingsSection(currentNote);
    var section = buildEngravingsNoteSection(cartItems);
    if (!section) return base;
    if (base) return base + '\n\n' + section;
    return section;
  }

  function buildEngravingAttributes(cartItems) {
    var sources = collectEngravingSources(cartItems);
    var attrs = {};
    sources.order.forEach(function (id) {
      var src = sources.byId[id];
      var item = src.item;
      var title = item.product_title || 'Item';
      var qty = !src.isFee && item.quantity ? ' × ' + item.quantity : '';
      var rows = engravingDetailRows(src.props);
      attrs['_engraving_' + id] = title + qty + ' — ' + rows.join(' | ');
    });
    return attrs;
  }

  /**
   * Diff the desired note + engraving attributes against current cart state.
   * Returns a /cart/update.js body ({note?, attributes?}) or null when in sync.
   */
  function composeUpdate(cartData) {
    var items = cartData.items || [];
    var body = {};

    var currentNote = cartData.note || '';
    var nextNote = composeNoteWithEngravings(currentNote, items);
    if (nextNote !== currentNote) body.note = nextNote;

    var nextAttrs = buildEngravingAttributes(items);
    var currentAttrs = cartData.attributes || {};
    Object.keys(currentAttrs).forEach(function (k) {
      if (k.indexOf('_engraving_') === 0 && !(k in nextAttrs)) nextAttrs[k] = '';
    });
    var attrChanges = {};
    var changedKeys = Object.keys(nextAttrs).filter(function (k) {
      return (currentAttrs[k] || '') !== nextAttrs[k];
    });
    if (changedKeys.length) {
      changedKeys.forEach(function (k) { attrChanges[k] = nextAttrs[k]; });
      body.attributes = attrChanges;
    }

    if (!('note' in body) && !('attributes' in body)) return null;
    return body;
  }

  /** Fetch cart, resync the ENGRAVINGS note section and _engraving_<id> attributes, POST only if changed. */
  function rebuildFromCart() {
    var cartGetUrl = (window.routes && window.routes.cart_url) || '/cart';
    if (cartGetUrl.indexOf('.js') === -1) cartGetUrl += '.js';
    var cartUpdateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
    if (cartUpdateUrl.indexOf('.js') === -1) cartUpdateUrl += '.js';
    return fetch(cartGetUrl)
      .then(function (r) { return r.json(); })
      .then(function (cartData) {
        var body = composeUpdate(cartData);
        if (!body) return;
        return fetch(cartUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
      })
      .catch(function () { /* non-critical */ });
  }

  window.EngravingNotes = {
    rebuildFromCart: rebuildFromCart,
    composeUpdate: composeUpdate
  };
})();
